import { useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { UpdateCountryPreference } from 'api/Clients/Types/UpdateCountryPreference'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { FilterCell } from 'components/Filter/FilterCell'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { normalizeFilter } from 'features/DataGrid'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchClientLanguagesAction } from 'store/clients/clients.actions'
import { pageableSettings } from 'store/grid/grid.slice'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import stringComparator from 'utils/stringComparator'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import {
  AdministrationCountriesNavigation,
  AdministrationCountriesTab,
} from './AdministrationCountriesNavigation'
import countriesColumns from './countriesColumns'
import EditCountryPage from './EditCountryPage'
import { normalizeCountries } from './normalizer'
import { CountryPreference } from './types'

const Countries = (): JSX.Element => {
  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const [countries, setCountries] = useState<CountryPreference[]>([])
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())
  const [country, setCountry] = useState<CountryPreference | null>(null)
  const [isModalOpen, setModalOpen] = useState<boolean>(false)

  const languages = useAppSelector((state) => state.clients.language)
  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)

  const [auditLogModal, setAuditLogModal] = useState<{
    visible: boolean
    data?: CountryPreference
  }>({
    visible: false,
  })
  const setToast = useContext(ToastContext)

  const dispatch = useAppDispatch()

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh((query?: PageQuery) => {
    return clientsApiClient.getCountriesPreference({
      ...query,
      displayAllCountries: true,
      showHiddenCountries: true,
    })
  }, [])
  const { data, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (data) {
      setCountries(normalizeCountries(data))
    }
    if (isEmpty(tickmillCompanies)) {
      dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
    if (isEmpty(languages)) {
      dispatch(fetchClientLanguagesAction(clientsApiClient))
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: UpdateCountryPreference) => {
    await clientsApiClient.updateCountyPreference(values)
    setToast(successToast('Country updated.'))
    setCountry(null)
    setModalOpen(false)
    forceRefresh()
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPaginationState({
      ...getPaginationInitialState(),
      skip: 0,
      take: paginationState.take,
    })
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    if (data) {
      setPaginationState({ skip: event.page.skip, take: event.page.take })
    }
  }

  const setAuditLog = (record: CountryPreference) => {
    setAuditLogModal({ data: record, visible: true })
  }

  const handleEdit = (countryPreference: CountryPreference) => {
    setCountry(countryPreference)
    setModalOpen(true)
  }

  if (isLoading) {
    return <Spinner />
  }

  const gridData = filterBy(countries || [], filter).sort(
    (a: CountryPreference, b: CountryPreference) => stringComparator(a.country.name, b.country.name)
  )

  return (
    <>
      {isModalOpen && country && (
        <EditCountryPage
          data={country}
          onCloseModal={() => setModalOpen(false)}
          onSubmit={onSubmit}
        />
      )}

      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false, data: undefined })}
          countryId={auditLogModal.data.country?.id ?? ''}
        />
      )}
      <Breadcrumbs crumbs={[{ title: 'Countries', link: links.administrationCountries.url }]} />
      <AdministrationCountriesNavigation tab={AdministrationCountriesTab.COUNTRIES} />
      <Grid
        filterable
        data={gridData.slice(paginationState.skip, paginationState.take + paginationState.skip)}
        filter={filter}
        skip={paginationState.skip}
        take={paginationState.take}
        total={gridData.length}
        onPageChange={handlePageChange}
        onFilterChange={handleFilterChange}
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        {countriesColumns
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx} {...column} />
          ))}
        {hasPermission(permissions, PermissionsEnum.CanEditCountryConfiguration) && (
          <Column
            width={50}
            title=''
            sortable={false}
            filterCell={FilterCell}
            cell={(props: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      'data-test': 'edit',
                      onClick: () => handleEdit(props.dataItem),
                    },
                    {
                      name: 'Audit Log',
                      'data-test': 'edit',
                      onClick: () => setAuditLog(props.dataItem),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        )}
      </Grid>
    </>
  )
}

export default Countries
